// The capability catalog as the web reads it. Until C2's generated catalog lands this is the contract fixture
// (same shape); C2 swaps the import for `generated/capability-catalog.v1.json` and nothing else changes.
import catalogJson from '../../../docs/fusegrid-everywhere/fixtures/capability-catalog.sample.json'

export type MinRole = 'viewer' | 'client_editor' | 'consultant_owner' | 'site_admin'
export interface CatalogExample { request: string; expected_arguments?: unknown }
export interface CatalogEntry {
  name: string
  title: string
  summary: string
  jobs: string[]
  effect: 'read' | 'write' | 'destructive' | 'host_op' | 'open_world'
  min_role: MinRole
  sidebar: 'server' | 'pane_local' | 'hidden'
  examples: CatalogExample[]
  help_id: string
}
export interface CatalogJob { id: string; title: string; summary: string }
export interface Catalog { schema_version: string; jobs: CatalogJob[]; entries: CatalogEntry[] }

export const CATALOG = catalogJson as unknown as Catalog

export const ROLE_ORDER: readonly MinRole[] = ['viewer', 'client_editor', 'consultant_owner', 'site_admin']
export const ROLE_LABELS: Record<MinRole, string> = {
  viewer: 'Viewer',
  client_editor: 'Client editor',
  consultant_owner: 'Advisor',
  site_admin: 'Firm admin',
}

/** Titles for the intent-map jobs (capability_catalog `Job`); the catalog's own job list wins when it has one. */
const JOB_TITLES: Record<string, string> = {
  client_setup: 'Set up a client',
  month_end_close: 'Month-end close',
  monthly_reporting: 'Monthly client report',
  cash: 'Cash',
  budget_forecast: 'Budget and forecast',
  performance: 'Performance and KPIs',
  views_and_data: 'Views and data',
  meeting_prep: 'Meeting prep',
  valuation_readiness: 'Valuation and readiness',
  job_finance: 'Job finance',
  client_workbook: 'Client workbook in Excel',
  orientation: 'Getting around',
}

export function jobTitle(catalog: Catalog, job: string): string {
  return catalog.jobs.find(j => j.id === job)?.title ?? JOB_TITLES[job] ?? job.replaceAll('_', ' ')
}

export const canUse = (entry: CatalogEntry, role: MinRole) => ROLE_ORDER.indexOf(entry.min_role) <= ROLE_ORDER.indexOf(role)

/** Entries the role may call, grouped by job in intent-map order, each entry under every job it serves. */
export function groupForRole(catalog: Catalog, role: MinRole, query = ''): { job: string; title: string; entries: CatalogEntry[] }[] {
  const needle = query.trim().toLowerCase()
  const matches = (entry: CatalogEntry) => !needle || [entry.title, entry.summary, ...entry.examples.map(e => e.request)].some(text => text.toLowerCase().includes(needle))
  const usable = catalog.entries.filter(entry => entry.sidebar !== 'hidden' && canUse(entry, role) && matches(entry))
  const order = Object.keys(JOB_TITLES)
  const jobs = [...new Set(usable.flatMap(entry => entry.jobs))].sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99))
  return jobs.map(job => ({ job, title: jobTitle(catalog, job), entries: usable.filter(entry => entry.jobs.includes(job)) }))
}

/** What to paste into Claude or ChatGPT: the request, naming FuseGrid so the assistant picks the connector. */
export const promptForAssistant = (request: string) => `Using FuseGrid: ${request}`
